import React, { useEffect, useState, useCallback } from 'react';
import { queueApi } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { FilterSelect } from './ui/FilterSelect';
import { TableFillBox, fillOrHugClass, tableFillHeightClass } from './ui/tableFillBox';
import { useBackdropDismiss } from '../hooks/useBackdropDismiss';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from './ui/table';

interface QueueJob {
  queue_id: string;
  user_id?: number;
  company_id?: number;
  job_type: string;
  job_source?: string;
  priority: number;
  status: string;
  analysis_type?: string;
  estimated_tokens?: number;
  actual_tokens_used?: number;
  processing_time_ms?: number;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  user_email?: string;
  company_name?: string;
  url_count?: number;
  prompt_name?: string;
  request_data?: any;
  result_data?: any;
  metadata?: any;
}

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'warn' | 'safe' | 'risk' | 'secondary'> = {
  pending: 'default',
  processing: 'warn',
  completed: 'safe',
  failed: 'risk',
  cancelled: 'secondary',
};

const QueueJobsList: React.FC = () => {
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [users, setUsers] = useState<Array<{user_id: string; user_email: string; job_count: number}>>([]);
  const [page, setPage] = useState(0);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);

  const limit = 20;
  const backdropDismiss = useBackdropDismiss(useCallback(() => setSelectedJob(null), []));

  // Table-fill contract (bd-n3uyh, shared TableFillBox — deepcontext-wbra):
  // a full page fills the table box down to the pagination row and scrolls
  // internally; a sparse page hugs its content. Keyed off the TOTAL result
  // count (`total`, already fetched via setTotal(data.total)) vs the page
  // size — NOT jobs.length, which is just the current page's row count and
  // would wrongly hug on the tail page of a multi-page list (e.g. total=45,
  // limit=20: page 3 returns only 5 rows, but the list is still multi-page).
  const isFullPage = total >= limit;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await queueApi.getJobs(filter || undefined, userFilter || undefined, limit, page * limit);
      setJobs(data.jobs as QueueJob[]);
      setTotal(data.total);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [filter, userFilter, page]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    // Fetch users on component mount
    queueApi.getJobUsers().then(setUsers).catch(console.error);
  }, []);

  const cancelJob = async (queueId: string) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) return;

    try {
      await queueApi.cancelJob(queueId);
      fetchJobs(); // Refresh list
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel job');
    }
  };

  const statusLabel = (status: string) => status.charAt(0).toUpperCase() + status.slice(1);

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <Card className={`${fillOrHugClass(isFullPage)} min-h-0 flex flex-col overflow-hidden`}>
        <CardContent className="flex-1 min-h-0 flex flex-col">
          <div className="flex-shrink-0 flex justify-between items-center gap-3 flex-wrap">
            <h2 className="text-sm font-extrabold text-ink">Queue Jobs</h2>

            <div className="flex gap-2 ml-auto">
              {/* User Filter */}
              <FilterSelect
                data-testid="queue-user-filter"
                value={userFilter}
                onChange={(value) => { setUserFilter(value); setPage(0); }}
                options={[
                  { value: '', label: 'All Users' },
                  ...users.map((user) => ({
                    value: user.user_id,
                    label: `${user.user_email} (${user.job_count})`,
                  })),
                ]}
              />

              {/* Status Filter */}
              <FilterSelect
                data-testid="queue-status-filter"
                value={filter}
                onChange={(value) => { setFilter(value); setPage(0); }}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading jobs...</div>
          ) : error ? (
            <div className="mt-4 bg-risk-bg border border-risk rounded-md p-4">
              <p className="text-risk">{error}</p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col mt-3">
              <TableFillBox isFullPage={isFullPage} className="overflow-x-auto" data-testid="queue-jobs-table-fill-box">
                <Table className={tableFillHeightClass(isFullPage)}>
                  <TableHead>
                    <tr>
                      <TableHeaderCell>Queue ID</TableHeaderCell>
                      <TableHeaderCell>User</TableHeaderCell>
                      <TableHeaderCell>URLs</TableHeaderCell>
                      <TableHeaderCell>Prompt</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow key={job.queue_id} className="cursor-pointer" onClick={() => setSelectedJob(job)}>
                        <TableCell className="font-mono font-bold text-accent-active">
                          {job.queue_id.slice(0, 8)}
                        </TableCell>
                        <TableCell className="text-ink-muted">{job.user_email || '-'}</TableCell>
                        <TableCell className="text-ink-muted">{job.url_count || 0}</TableCell>
                        <TableCell className="text-ink-muted">{job.prompt_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_BADGE_VARIANT[job.status] || 'secondary'}>{statusLabel(job.status)}</Badge>
                        </TableCell>
                        <TableCell className="text-ink-muted">{new Date(job.created_at || '').toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableFillBox>

              {/* Pagination */}
              <div className="flex-shrink-0 mt-4 flex justify-between items-center">
                <div className="text-xs text-ink-muted">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} jobs
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
                    Previous
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Details Modal — sections as canvas insets, error as a risk
          inset, request JSON in a terminal-dark code block (bd deepcontext-2951). */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" {...backdropDismiss}>
          <div
            role="dialog"
            aria-modal="true"
            className="bg-surface rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-line"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-ink">Job Details</h3>
                {(selectedJob.status === 'pending' || selectedJob.status === 'processing') && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      cancelJob(selectedJob.queue_id);
                      setSelectedJob(null);
                    }}
                  >
                    Cancel Job
                  </Button>
                )}
              </div>

              {/* Basic Info */}
              <div className="bg-canvas rounded-xl p-3 mb-3">
                <h4 className="font-bold text-ink text-xs mb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-ink-muted">Queue ID:</span> {selectedJob.queue_id}</div>
                  <div><span className="text-ink-muted">Status:</span> <Badge variant={STATUS_BADGE_VARIANT[selectedJob.status] || 'secondary'}>{statusLabel(selectedJob.status)}</Badge></div>
                  <div><span className="text-ink-muted">User:</span> {selectedJob.user_email || '-'}</div>
                  <div><span className="text-ink-muted">Company:</span> {selectedJob.company_name || '-'}</div>
                  <div><span className="text-ink-muted">URLs:</span> {selectedJob.url_count || 0}</div>
                  <div><span className="text-ink-muted">Prompt:</span> {selectedJob.prompt_name || '-'}</div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="bg-canvas rounded-xl p-3 mb-3">
                <h4 className="font-bold text-ink text-xs mb-2">Technical Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-ink-muted">Type:</span> {selectedJob.job_type}</div>
                  <div><span className="text-ink-muted">Analysis Type:</span> {selectedJob.analysis_type || '-'}</div>
                  <div><span className="text-ink-muted">Priority:</span> {selectedJob.priority}</div>
                  <div><span className="text-ink-muted">Source:</span> {selectedJob.job_source || '-'}</div>
                  <div><span className="text-ink-muted">Estimated Tokens:</span> {selectedJob.estimated_tokens?.toLocaleString() || '-'}</div>
                  <div><span className="text-ink-muted">Actual Tokens:</span> {selectedJob.actual_tokens_used?.toLocaleString() || '-'}</div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="bg-canvas rounded-xl p-3 mb-3">
                <h4 className="font-bold text-ink text-xs mb-2">Timestamps</h4>
                <div className="space-y-1 text-sm">
                  <div><span className="text-ink-muted">Created:</span> {formatDate(selectedJob.created_at)}</div>
                  <div><span className="text-ink-muted">Started:</span> {formatDate(selectedJob.started_at)}</div>
                  <div><span className="text-ink-muted">Completed:</span> {formatDate(selectedJob.completed_at)}</div>
                  <div><span className="text-ink-muted">Processing Time:</span> {formatDuration(selectedJob.processing_time_ms)}</div>
                </div>
              </div>

              {/* Error Message */}
              {selectedJob.error_message && (
                <div className="bg-risk-bg rounded-xl p-3 mb-3">
                  <h4 className="font-bold text-risk text-xs mb-2">Error</h4>
                  <div className="text-sm text-risk">{selectedJob.error_message}</div>
                </div>
              )}

              {/* Request Data */}
              {selectedJob.request_data && (
                <div className="mb-3">
                  <h4 className="font-bold text-ink text-xs mb-2">Request Data</h4>
                  <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
                    <pre className="text-success-500 font-mono text-xs whitespace-pre-wrap">
                      {JSON.stringify(selectedJob.request_data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              <Button variant="secondary" size="sm" className="mt-2" onClick={() => setSelectedJob(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueJobsList;
